import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { APP_CONFIG } from '../../constants/appConfig';
import { X, Home, Users, Package, Barcode, FileText, Settings, ChevronRight, LogOut, ChevronLeft } from 'lucide-react';

const SidePanel = ({ isOpen, onClose }) => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, setIsCollapsed } = useSidebar();

  const menuItems = {
    ADMIN: [
      { label: 'Dashboard', icon: Home, path: '/dashboard' },
      { label: 'Users', icon: Users, path: '/users' },
      { label: 'Barcodes', icon: Barcode, path: '/barcodes' },
      { label: 'Import', icon: Package, path: '/import' },
      { label: 'Records', icon: FileText, path: '/records' },
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
    VEHICLE_EXECUTIVE: [
      { label: 'Dashboard', icon: Home, path: '/dashboard' },
      { label: 'Scan Barcode', icon: Barcode, path: '/scan' },
      { label: 'Create Record', icon: FileText, path: '/create-record' },
      { label: 'My Records', icon: Package, path: '/records' },
    ],
    SERVICE_ENGINEER: [
      { label: 'Dashboard', icon: Home, path: '/dashboard' },
      { label: 'Scan & Update', icon: Barcode, path: '/scan-update' },
      { label: 'My Records', icon: FileText, path: '/records' },
    ],
  };

  const items = menuItems[user?.role] || menuItems.VEHICLE_EXECUTIVE;
  const isActive = (path) => location.pathname === path;

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Backdrop Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 md:hidden z-40 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
      )}

      {/* Side Panel */}
      <aside
        className={`fixed md:static z-50 h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white flex flex-col transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 shadow-2xl md:shadow-none' : '-translate-x-full md:translate-x-0'
        } w-72 ${isCollapsed ? 'md:w-20' : 'md:w-72'} top-0 left-0`}
      >
        {/* Header */}
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center justify-between mb-4">
            {/* Always show expanded on mobile, respect collapsed state on desktop */}
            <div className="flex items-center gap-3 flex-1 min-w-0 md:hidden">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Barcode className="w-7 h-7 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white truncate">{APP_CONFIG.appName}</h1>
                <p className="text-xs text-slate-400 truncate">{APP_CONFIG.description}</p>
              </div>
            </div>
            {/* Desktop: Show based on collapsed state */}
            {!isCollapsed && (
              <div className="hidden md:flex items-center gap-3 flex-1 min-w-0">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Barcode className="w-7 h-7 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold text-white truncate">{APP_CONFIG.appName}</h1>
                  <p className="text-xs text-slate-400 truncate">{APP_CONFIG.description}</p>
                </div>
              </div>
            )}
            {isCollapsed && (
              <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl items-center justify-center shadow-lg">
                <Barcode className="w-7 h-7 text-white" />
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden md:block p-2 hover:bg-slate-800 rounded-lg transition-colors"
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isCollapsed ? (
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                ) : (
                  <ChevronLeft className="w-5 h-5 text-slate-300" />
                )}
              </button>
              <button
                onClick={onClose}
                className="md:hidden p-2 hover:bg-slate-800 rounded-lg transition-colors"
                aria-label="Close sidebar"
              >
                <X className="w-5 h-5 text-slate-300" />
              </button>
            </div>
          </div>

          {/* User Info */}
          {!isCollapsed && (
            <div className="bg-slate-800 bg-opacity-50 rounded-lg p-3 border border-slate-700">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Logged in as</p>
              <p className="text-sm font-semibold text-white mt-1">{user?.fullName}</p>
              <p className="text-xs text-blue-400 mt-1">
                {user?.role?.replace(/_/g, ' ')}
              </p>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-4 py-6">
          <div className="space-y-1">
            {items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  title={isCollapsed ? item.label : ''}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  } ${isCollapsed ? 'md:justify-center justify-start' : 'justify-between'}`}
                >
                  <div className={`flex items-center gap-3 ${isCollapsed ? 'md:w-auto w-full' : 'flex-1'}`}>
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className={`font-medium text-sm truncate ${isCollapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                  </div>
                  {active && !isCollapsed && <ChevronRight className="w-4 h-4 text-white flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-slate-800 bg-slate-950 bg-opacity-50">
          {isCollapsed ? (
            <button
              onClick={logout}
              className="w-full flex items-center justify-center p-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          ) : (
            <>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors mb-4 font-medium text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
              <p className="text-xs text-slate-500 text-center">{APP_CONFIG.copyright}</p>
              <p className="text-xs text-slate-600 text-center mt-1">{APP_CONFIG.fullBrand}</p>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default SidePanel;
